import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { squidloansUserAPI } from "../../services/api";
import { Upload, Eye, EyeOff, FileText, Lock, Loader2 } from "lucide-react";
import Toast from "../../components/Toast";
import useLeadStage from "../../hooks/useLeadStage";
import { LEAD_STAGE, automationNodeLeadStageToRouteMap } from "../../Utils/constants";
import FlowFooter from "../../components/FlowFooter";

const curr_page_lead_stage = [
  LEAD_STAGE.AA_FAILED,
  LEAD_STAGE.VERIFY_EMAIL_OTP
];

export default function UploadBankStatement() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [maxLoanAmount, setMaxLoanAmount] = useState(0);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { leadStage, isLoadingStage } = useLeadStage();
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "error",
  });
  const [errorModal, setErrorModal] = useState({
    show: false,
    message: "",
  });

  useEffect(() => {
    if (
      leadStage &&
      !isLoadingStage &&
      !curr_page_lead_stage.includes(leadStage)
    ) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
  }, [leadStage, isLoadingStage, navigate]);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setIsUploading(false);
      setUploadError(null);
      setIsPasswordProtected(false);
      setPassword("");
    }
  };

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  const showErrorModal = (message) => {
    setErrorModal({ show: true, message });
  };

  const hideErrorModal = () => {
    setErrorModal({ show: false, message: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    hideToast();
    if (!selectedFile) {
      showToast("Please select a file to upload.");
      return;
    }
    if (isPasswordProtected && !password) {
      showToast("Please enter the password for your statement.");
      return;
    }

    setIsUploading(true);
    try {
      const response = await squidloansUserAPI.uploadBankStatement(
        selectedFile,
        isPasswordProtected ? password : null
      );
      if (response?.is_hard_reject) {
        navigate("/apply/rejected");
        return;
      }

      else if (response?.is_bre_reject) {
        navigate("/apply/thank-you");
        return;
      }

      if (response.maxLoanAmount) {
        const newMaxAmount = parseInt(response.maxLoanAmount);
        setMaxLoanAmount(newMaxAmount);
      }

      navigate("/apply/loan-amount");
      // setShowCongratsModal(true);
    } catch (error) {
      const errorMessage = error.message;

      showToast(errorMessage);
      showErrorModal(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseModal = () => {
    setShowCongratsModal(false);
    navigate("/apply/loan-amount");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-2xl w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-4 sm:mb-6 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              Upload your <span className="text-[#FF6B35]">bank statement</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-[#64748B]">
              We've reviewed your application — Money is shining for you! But first, a small ritual—your bank statement!
            </p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <div className="w-full">
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl sm:rounded-2xl px-3 sm:px-4 py-3 sm:py-4 mb-3 sm:mb-4">
                <p className="text-xs sm:text-sm text-[#1E293B] font-semibold mb-1">Choose your preferred method</p>
                <p className="text-[10px] sm:text-xs text-[#64748B]">
                  Want a Higher Loan Amount? Upload your bank statement or use net banking to unlock bigger funds Instantly!
                </p>
              </div>

              {!selectedFile ? (
                <div
                  onClick={handleUploadClick}
                  className="group flex items-center justify-center w-full h-24 sm:h-28 border-2 border-dashed border-[#E2E8F0] rounded-xl sm:rounded-2xl bg-[#F8FAFC] hover:bg-white hover:border-[#FF6B35]/50 cursor-pointer transition-all duration-300"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF6B35]" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-[#FF6B35]">Upload Statement</span>
                  </div>
                </div>
              ) : (
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center">
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF6B35]" />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-semibold text-[#1E293B]">
                          {selectedFile.name.length > 25
                            ? selectedFile.name.substring(0, 25) + "..."
                            : selectedFile.name}
                        </h3>
                        <p className="text-[10px] sm:text-xs text-[#64748B]">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="text-[#64748B] hover:text-red-500 transition-colors p-1"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-3 sm:space-y-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPasswordProtected}
                        onChange={(e) => setIsPasswordProtected(e.target.checked)}
                        className="w-4 h-4 rounded border-[#E2E8F0] bg-white focus:ring-[#FF6B35]/40 text-[#FF6B35]"
                      />
                      <span className="text-xs text-[#64748B]">Password-protected statement</span>
                    </label>

                    {isPasswordProtected && (
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter password"
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white border border-[#E2E8F0] text-sm sm:text-base text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 focus:border-[#FF6B35] pr-10 sm:pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#1E293B]"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    )}

                    {uploadError && (
                      <p className="text-xs text-red-500 text-center bg-red-50 border border-red-200 rounded-xl py-2">
                        {uploadError}
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isUploading}
                      className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                          Upload Bank Statement
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </div>

      {isUploading && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-50">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-[#E2E8F0] border-t-[#FF6B35] border-r-[#FF6B35] animate-spin mb-4 sm:mb-6" />
          <h2 className="text-base sm:text-lg font-semibold text-[#1E3A8A] mb-1">Uploading your statement</h2>
          <p className="text-xs sm:text-sm text-[#64748B]">Please wait while we process your bank statement...</p>
        </div>
      )}

      {showCongratsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl w-full max-w-[300px] mx-4 overflow-hidden shadow-2xl">
            <div className="p-4 sm:p-6 flex flex-col items-center bg-gradient-to-br from-[#FF6B35]/10 to-[#1E3A8A]/10">
              <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center mb-3 sm:mb-4">
                <span className="text-2xl sm:text-4xl">🎉</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#1E3A8A] mb-2">BOOM!</h2>
              <p className="text-lg sm:text-xl font-semibold text-[#FF6B35]">₹{maxLoanAmount.toLocaleString()}</p>
              <p className="text-xs sm:text-sm text-[#64748B]">unlocked for you!</p>
            </div>
            <div className="p-3 sm:p-4">
              <button
                onClick={handleCloseModal}
                className="w-full py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all shadow-md"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}

      {errorModal.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl w-full max-w-[400px] mx-4 overflow-hidden shadow-2xl">
            <div className="p-4 sm:p-6 flex flex-col items-center relative bg-gradient-to-br from-red-50 to-transparent">
              <button
                onClick={hideErrorModal}
                className="absolute top-3 sm:top-4 right-3 sm:right-4 text-[#64748B] hover:text-[#1E293B] transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 border-2 border-red-500 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-[#1E3A8A] mb-2">Oops! Something went wrong</h2>
            </div>
            <div className="p-4 sm:p-6">
              <p className="text-[#64748B] text-center mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{errorModal.message}</p>
              <button
                onClick={hideErrorModal}
                className="w-full py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all shadow-md"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      <FlowFooter />
    </div>
  );
}
